import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { ArrowLeft, Save, Calendar, MapPin, Youtube, Upload, Settings, DollarSign, Clock, CheckCircle, Search, X } from 'lucide-react'
import { useToast } from '../../hooks/useToast'
import Toast from '../../components/ui/Toast'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import leiloesService from '../../services/leiloesService'
import organizadoresService from '../../services/organizadoresService'
import clientesService from '../../services/clientesService'
import { comissoesService } from '../../services/comissoesService'
import ComissaoBeneficenteOpcao from '../../components/leiloes/ComissaoBeneficenteOpcao'
import leiloeiroService from '../../services/leiloeiroService'
import NovaComissaoModal from '../../components/modals/NovaComissaoModal'
import NovoLeiloeiroModal from '../../components/modals/NovoLeiloeiroModal'
import EditarComissaoModal from '../../components/modals/EditarComissaoModal'
import EditarLeiloeiroModal from '../../components/modals/EditarLeiloeiroModal'
import PromotoresLeilao from '../../components/leiloes/PromotoresLeilao'
import FaixasDefesaLeilao from '../../components/comissoes/FaixasDefesaLeilao'
import NovoMeioPagamentoModal from '../../components/modals/NovoMeioPagamentoModal'
import { meiosPagamentoService } from '../../services/meiosPagamentoService'

export default function EditarLeilaoModerno() {
  const { id } = useParams()
  const { toasts, success, error, hideToast } = useToast()
  const { organizadorAtivo } = useOrganizador()
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [currentStep, setCurrentStep] = useState(1)
  const [tiposDisponiveis, setTiposDisponiveis] = useState([])
  const [cidades, setCidades] = useState([])
  const [searchCidade, setSearchCidade] = useState('')
  const [showDataFim, setShowDataFim] = useState(false)
  const [organizadorConfig, setOrganizadorConfig] = useState(null)
  const [showNovaComissao, setShowNovaComissao] = useState(false)
  const [showNovoLeiloeiro, setShowNovoLeiloeiro] = useState(false)
  const [loadingComissao, setLoadingComissao] = useState(false)
  const [loadingLeiloeiro, setLoadingLeiloeiro] = useState(false)
  const [showEditComissao, setShowEditComissao] = useState(false)
  const [showEditLeiloeiro, setShowEditLeiloeiro] = useState(false)
  const [meiosPagamento, setMeiosPagamento] = useState([])
  const [showNovoMeioPagamento, setShowNovoMeioPagamento] = useState(false)
  const [leiloeirosList, setLeiloeirosList] = useState([])
  const [doisLeiloeiros, setDoisLeiloeiros] = useState(false)
  const [loadingLeiloeirosList, setLoadingLeiloeirosList] = useState(false)
  const [dadosBancarios, setDadosBancarios] = useState([])
  // Guarda os valores GTA/Nota como vieram da API (antes do `|| 0`), para diferenciar
  // "leilão nunca configurado" de "leilão configurado com 0 de propósito"
  const leilaoGtaRawRef = useRef({ valor_por_animal: null, valor_nota: null, valor_gta: null, gta_modo_cobranca: null })

  const [formData, setFormData] = useState({
    codigo_evento: '',
    nome: '',
    data_leilao: '',
    hora_inicio: '',
    data_fim: '',
    hora_fim: '',
    cidade: '',
    uf: '',
    local: '',
    tipo_leilao: 'gado_corte',
    status: 'on',
    link_transmissao: '',
    banner_file: null,
    banner_url: '',
    catalogo_link: '',
    fluxo_comissao: '',
    comissao_padrao_id: '',
    comissao_respeitar_individual: false,
    comissao_venda: '',
    comissao_compra: '',
    comissao_defesa: '',
    leiloeiro_id: '',
    leiloeiro_perc_venda: '',
    leiloeiro_perc_defesa: '',
    leiloeiro2_id: '',
    leiloeiro2_perc_venda: '',
    leiloeiro2_perc_defesa: '',
    valor_gta: 0,
    valor_nota: 0,
    valor_por_animal: 0,
    valor_taxa: 0,
    gta_modo_cobranca: '',
    regra_faturamento: 'por_cabeca',
    meio_pagamento_padrao_id: '',
    dado_bancario_organizador_id: '',
    horario_limite_entrada: '',
    bloquear_sync_gestao: false,
    defesa_modo: 'tabela',
    taxa_inscricao_valor: ''
  })
  
  const steps = [
    { id: 1, name: 'Básico', icon: Settings, description: 'Nome e tipo do leilão' },
    { id: 2, name: 'Local & Data', icon: MapPin, description: 'Onde e quando será' },
    { id: 3, name: 'Mídia', icon: Youtube, description: 'Transmissão e catálogo' },
    { id: 4, name: 'Financeiro', icon: DollarSign, description: 'Comissões e valores' }
  ]

  const organizadorId = organizadorAtivo?.id
  const isOrgFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo'
  const tiposParaExibir = (() => {
    const base = isOrgFluxoCavalo
      ? tiposDisponiveis.filter(t => t === 'cavalo')
      : tiposDisponiveis.filter(t => t !== 'cavalo')
    // Preserva o tipo atual do leilão mesmo que normalmente estivesse oculto (dado legado)
    if (formData.tipo_leilao && !base.includes(formData.tipo_leilao)) {
      return [...base, formData.tipo_leilao]
    }
    return base
  })()

  useEffect(() => {
    if (organizadorId && id) {
      loadLeilao()
      loadTiposDisponiveis()
      loadOrganizadorConfig()
    }
  }, [organizadorId, id])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchCidade) {
        searchCidades(searchCidade)
      }
    }, 300)
    
    return () => clearTimeout(timeoutId)
  }, [searchCidade])

  // Preenche dados da comissão quando leilão e config estão prontos
  useEffect(() => {
    if (!formData.comissao_padrao_id || !organizadorConfig?.comissoes?.length) return
    const comissao = organizadorConfig.comissoes.find(c => String(c.id) === String(formData.comissao_padrao_id))
    if (comissao) {
      setFormData(prev => ({
        ...prev,
        comissao_venda:  comissao.comissao_venda,
        comissao_compra: comissao.comissao_compra,
        comissao_defesa: comissao.comissao_defesa,
      }))
    }
  }, [formData.comissao_padrao_id, organizadorConfig])

  const loadLeiloeirosList = async (primaryLeiloeiroId = null) => {
    if (!organizadorId || !id) return
    try {
      const lista = await leiloeiroService.listarDoLeilao(organizadorId, id)
      setLeiloeirosList(lista)
      if (lista.length < 2) return
      // Usar o ID primário passado diretamente (evita race condition com formData)
      setFormData(prev => {
        const primaryId = primaryLeiloeiroId ? String(primaryLeiloeiroId) : String(prev.leiloeiro_id)
        const segundo = lista.find(ll => String(ll.leiloeiro_id) !== primaryId)
        if (!segundo) return prev
        setDoisLeiloeiros(true)
        return {
          ...prev,
          leiloeiro2_id:          String(segundo.leiloeiro_id),
          leiloeiro2_perc_venda:  segundo.perc_venda  ?? '',
          leiloeiro2_perc_defesa: segundo.perc_defesa ?? '',
        }
      })
    } catch (e) {
      console.error('Erro ao carregar leiloeiros do leilão:', e)
    }
  }

  const handleAdicionarLeiloeiroAoLeilao = async () => {
    if (!novoLeiloeiroForm.leiloeiro_id) return
    setLoadingLeiloeirosList(true)
    try {
      await leiloeiroService.adicionarAoLeilao(organizadorId, id, {
        leiloeiro_id: parseInt(novoLeiloeiroForm.leiloeiro_id),
        perc_venda:   parseFloat(novoLeiloeiroForm.perc_venda)  || 0,
        perc_defesa:  parseFloat(novoLeiloeiroForm.perc_defesa) || 0,
      })
      await loadLeiloeirosList()
      setNovoLeiloeiroForm({ leiloeiro_id: '', perc_venda: '', perc_defesa: '' })
      setShowAdicionarLeiloeiro(false)
      success('Leiloeiro adicionado ao leilão')
    } catch (e) {
      error(e.response?.data?.detail || 'Erro ao adicionar leiloeiro')
    } finally {
      setLoadingLeiloeirosList(false)
    }
  }

  const handleRemoverLeiloeiroDoLeilao = async (llId) => {
    if (!window.confirm('Remover este leiloeiro do leilão?')) return
    setLoadingLeiloeirosList(true)
    try {
      await leiloeiroService.removerDoLeilao(organizadorId, id, llId)
      await loadLeiloeirosList()
      success('Leiloeiro removido')
    } catch (e) {
      error(e.response?.data?.detail || 'Erro ao remover leiloeiro')
    } finally {
      setLoadingLeiloeirosList(false)
    }
  }

  const handleAtualizarPercLeiloeiro = async (ll, campo, valor) => {
    try {
      await leiloeiroService.atualizarNoLeilao(organizadorId, id, ll.id, {
        leiloeiro_id: ll.leiloeiro_id,
        perc_venda:   campo === 'perc_venda'  ? parseFloat(valor) || 0 : ll.perc_venda,
        perc_defesa:  campo === 'perc_defesa' ? parseFloat(valor) || 0 : ll.perc_defesa,
      })
      await loadLeiloeirosList()
    } catch (e) {
      error(e.response?.data?.detail || 'Erro ao atualizar percentual')
    }
  }

  const loadLeilao = async () => {
    try {
      setLoadingData(true)
      const leilao = await leiloesService.getById(organizadorId, id)

      leilaoGtaRawRef.current = {
        valor_por_animal: leilao.valor_por_animal,
        valor_nota: leilao.valor_nota,
        valor_gta: leilao.valor_gta,
        gta_modo_cobranca: leilao.gta_modo_cobranca,
      }

      // Converter dados para o formato do formulário
      const formatDateForInput = (dateString) => {
        if (!dateString) return ''
        return dateString.split('T')[0] // Pegar apenas a parte da data
      }
      
      setFormData({
        codigo_evento: leilao.codigo_evento || '',
        nome: leilao.nome || '',
        data_leilao: formatDateForInput(leilao.data_leilao),
        hora_inicio: leilao.hora_inicio || '',
        data_fim: formatDateForInput(leilao.data_fim),
        hora_fim: leilao.hora_fim || '',
        cidade: leilao.cidade || '',
        uf: leilao.uf || '',
        local: leilao.local || '',
        tipo_leilao: leilao.tipo_leilao || 'gado_corte',
        status: leilao.status || 'on',
        link_transmissao: leilao.link_transmissao || '',
        banner_file: null,
        banner_url: leilao.banner_url ?
          (leilao.banner_url.startsWith('http') ? leilao.banner_url : `${(import.meta.env.VITE_API_URL || 'http://localhost:8001/').replace(/\/$/, '')}${leilao.banner_url}`)
          : '',
        catalogo_link: leilao.catalogo_link || '',
        fluxo_comissao: leilao.fluxo_comissao || '',
        comissao_padrao_id: leilao.comissao_padrao_id ? String(leilao.comissao_padrao_id) : '',
        comissao_respeitar_individual: !!leilao.comissao_respeitar_individual,
        comissao_venda: '',
        comissao_compra: '',
        comissao_defesa: '',
        leiloeiro_id: leilao.leiloeiro_id ? String(leilao.leiloeiro_id) : '',
        leiloeiro_perc_venda: leilao.leiloeiro_perc_venda || '',
        leiloeiro_perc_defesa: leilao.leiloeiro_perc_defesa || '',
        valor_gta: leilao.valor_gta || 0,
        valor_nota: leilao.valor_nota || 0,
        valor_por_animal: leilao.valor_por_animal || 0,
        valor_taxa: leilao.valor_taxa || 0,
        gta_modo_cobranca: leilao.gta_modo_cobranca || '',
        regra_faturamento: leilao.regra_faturamento || 'por_cabeca',
        meio_pagamento_padrao_id: leilao.meio_pagamento_padrao_id || '',
        dado_bancario_organizador_id: leilao.dado_bancario_organizador_id ? String(leilao.dado_bancario_organizador_id) : '',
        horario_limite_entrada: leilao.horario_limite_entrada || '',
        bloquear_sync_gestao: leilao.bloquear_sync_gestao || false,
        defesa_modo: leilao.defesa_modo || 'tabela',
        taxa_inscricao_valor: leilao.taxa_inscricao_valor ?? ''
      })
      
      // Carregar leiloeiros do leilão passando o ID primário para evitar race condition
      loadLeiloeirosList(leilao.leiloeiro_id)

      // dados da comissão serão preenchidos pelo useEffect abaixo quando organizadorConfig carregar

      // Configurar busca de cidade
      setSearchCidade(leilao.cidade || '')
      
      // Mostrar data fim se existir
      setShowDataFim(!!(leilao.data_fim || leilao.hora_fim))
      
      // Debug banner
      console.log('Banner URL carregada:', leilao.banner_url)
      
    } catch (err) {
      console.error('Erro ao carregar leilão:', err)
      error('Erro ao carregar dados do leilão')
    } finally {
      setLoadingData(false)
    }
  }

  const loadTiposDisponiveis = async () => {
    try {
      const tipos = await leiloesService.getTiposDisponiveis(organizadorId)
      setTiposDisponiveis(tipos.todos || [])
    } catch (err) {
      console.error('Erro ao carregar tipos:', err)
      setTiposDisponiveis(['gado_corte', 'beneficente'])
    }
  }

  const loadOrganizadorConfig = async () => {
    try {
      const [comissoes, leiloeiros, meiosPagamentoResponse, gtaConfig, dadosBancariosResponse] = await Promise.all([
        comissoesService.listar(organizadorId, { ativo: 'true' }),
        leiloeiroService.getByOrganizador(organizadorId),
        meiosPagamentoService.listar(organizadorId),
        organizadoresService.getConfiguracaoGtaNota(organizadorId).catch(() => null),
        organizadoresService.getDadosBancarios(organizadorId).catch(() => [])
      ])
      
      const meiosPag = meiosPagamentoResponse?.data || meiosPagamentoResponse || []
      setMeiosPagamento(meiosPag)
      
      const dadosBanc = (dadosBancariosResponse?.data || dadosBancariosResponse || []).filter(d => d.is_active)
      setDadosBancarios(dadosBanc)
      
      const usaComissaoSocios = organizadorAtivo?.usa_comissao_socios || organizadorAtivo?.produtos_premium?.includes('comissao_socios')
      
      const config = {
        fluxo_comissao_padrao: 'primeira_parcela',
        comissoes: comissoes?.data || comissoes || [],
        leiloeiros: leiloeiros?.data || leiloeiros || [],
        usa_comissao_socios: usaComissaoSocios
      }
      
      setOrganizadorConfig(config)

      // Preencher GTA/Nota com config do org somente quando o leilão nunca teve esses campos
      // configurados (null/undefined). Se o leilão já tem um valor salvo (mesmo que 0), respeita-o.
      if (gtaConfig) {
        const raw = leilaoGtaRawRef.current
        setFormData(prev => ({
          ...prev,
          valor_por_animal:  raw.valor_por_animal  ?? gtaConfig.gta_valor_por_animal   ?? prev.valor_por_animal,
          valor_nota:        raw.valor_nota         ?? gtaConfig.gta_valor_por_gta_nota ?? prev.valor_nota,
          valor_gta:         raw.valor_gta          ?? gtaConfig.gta_valor_por_animal   ?? prev.valor_gta,
          gta_modo_cobranca: raw.gta_modo_cobranca  || gtaConfig.gta_modo_cobranca      || '',
        }))
      }
    } catch (err) {
      console.error('Erro ao carregar configuração:', err)
      setOrganizadorConfig({
        fluxo_comissao_padrao: 'primeira_parcela',
        comissoes: [],
        leiloeiros: []
      })
    }
  }

  const searchCidades = async (termo) => {
    if (termo.length < 2) {
      setCidades([])
      return
    }
    
    try {
      const response = await clientesService.searchCidades(termo)
      setCidades(response.slice(0, 10))
    } catch (err) {
      console.error('Erro ao buscar cidades:', err)
      setCidades([])
    }
  }

  const handleCidadeSelect = (cidade) => {
    setFormData({
      ...formData,
      cidade: cidade.nome,
      uf: cidade.uf
    })
    setSearchCidade(cidade.nome)
    setCidades([])
  }

  const handleBannerUpload = (event) => {
    event.preventDefault()
    event.stopPropagation()
    
    const file = event.target.files[0]
    if (file) {
      console.log('Arquivo selecionado:', file.name, file.type, file.size)
      
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        error('Por favor, selecione apenas arquivos de imagem')
        return
      }
      
      // Validar tamanho (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        error('Arquivo muito grande. Máximo 5MB')
        return
      }
      
      console.log('Arquivo válido, atualizando formData')
      setFormData({ ...formData, banner_file: file, banner_url: '' })
      success('Arquivo selecionado com sucesso!')
    }
    
    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    event.target.value = ''
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!organizadorId) {
      error('Nenhum organizador selecionado')
      return
    }

    // Validações específicas
    if (!formData.cidade?.trim()) {
      error('Cidade é obrigatória')
      return
    }
    
    if (!formData.uf?.trim()) {
      error('UF é obrigatória')
      return
    }

    if (!formData.hora_inicio?.trim()) {
      error('Hora de início do leilão é obrigatória')
      return
    }

    if (formData.tipo_leilao !== 'beneficente') {
      if (!formData.comissao_padrao_id) {
        error('Comissão é obrigatória para este tipo de leilão')
        return
      }
      
    }

    // Validações para regra de faturamento por parcela
    if (formData.regra_faturamento === 'por_parcela') {
      if (!formData.meio_pagamento_padrao_id) {
        error('Selecione um meio de pagamento para faturamento por parcela')
        return
      }
    }

    try {
      setLoading(true)
      
      const leilaoData = {
        codigo_evento: formData.codigo_evento,
        nome: formData.nome,
        data_leilao: formData.data_leilao,
        hora_inicio: formData.hora_inicio,
        data_fim: formData.data_fim || null,
        hora_fim: formData.hora_fim || null,
        cidade: formData.cidade || null,
        uf: formData.uf || null,
        local: formData.local || null,
        tipo_leilao: formData.tipo_leilao,
        status: formData.status,
        link_transmissao: formData.link_transmissao || null,
        banner_url: formData.banner_url || null, // Por enquanto manter URL
        catalogo_link: formData.catalogo_link || null,
        fluxo_comissao: formData.fluxo_comissao || null,
        valor_gta: parseFloat(formData.valor_por_animal) || 0,
        valor_nota: parseFloat(formData.valor_nota) || 0,
        valor_por_animal: parseFloat(formData.valor_por_animal) || 0,
        valor_taxa: parseFloat(formData.valor_taxa) || 0,
        gta_modo_cobranca: formData.gta_modo_cobranca || null,
        comissao_padrao_id: parseInt(formData.comissao_padrao_id) || null,
        comissao_respeitar_individual: formData.tipo_leilao === 'beneficente' ? !!formData.comissao_respeitar_individual : false,
        leiloeiro_id: parseInt(formData.leiloeiro_id) || null,
        leiloeiro_perc_venda: parseFloat(formData.leiloeiro_perc_venda) || null,
        leiloeiro_perc_defesa: parseFloat(formData.leiloeiro_perc_defesa) || null,
        regra_faturamento: formData.regra_faturamento || 'por_cabeca',
        meio_pagamento_padrao_id: formData.regra_faturamento === 'por_parcela' ? parseInt(formData.meio_pagamento_padrao_id) || null : null,
        dado_bancario_organizador_id: parseInt(formData.dado_bancario_organizador_id) || null,
        horario_limite_entrada: formData.horario_limite_entrada || null,
        bloquear_sync_gestao: !!formData.bloquear_sync_gestao,
        ...(formData.tipo_leilao === 'cavalo' ? {
          defesa_modo: formData.defesa_modo || 'tabela',
          taxa_inscricao_valor: formData.taxa_inscricao_valor === '' ? null : parseFloat(formData.taxa_inscricao_valor)
        } : {})
      }
      
      // Remover banner_file dos dados (será enviado separadamente)
      delete leilaoData.banner_file
      
      await leiloesService.update(organizadorId, id, leilaoData)

      // Sincronizar leiloeiros em leilao_leiloeiros (upsert por leiloeiro)
      const leiloeirosSalvar = []
      if (leilaoData.leiloeiro_id) leiloeirosSalvar.push({
        leiloeiro_id: leilaoData.leiloeiro_id,
        perc_venda:   leilaoData.leiloeiro_perc_venda  || 0,
        perc_defesa:  leilaoData.leiloeiro_perc_defesa || 0,
      })
      if (formData.leiloeiro2_id) leiloeirosSalvar.push({
        leiloeiro_id: parseInt(formData.leiloeiro2_id),
        perc_venda:   parseFloat(formData.leiloeiro2_perc_venda)  || 0,
        perc_defesa:  parseFloat(formData.leiloeiro2_perc_defesa) || 0,
      })

      // Remover leiloeiros que foram desmarcados
      try {
        const listaAtual = await leiloeiroService.listarDoLeilao(organizadorId, id)
        const idsNovos = leiloeirosSalvar.map(ll => ll.leiloeiro_id)
        for (const ll of listaAtual) {
          if (!idsNovos.includes(ll.leiloeiro_id)) {
            await leiloeiroService.removerDoLeilao(organizadorId, id, ll.id).catch(() => {})
          }
        }
      } catch (e) { console.error('Erro ao limpar leiloeiros:', e) }

      for (const ll of leiloeirosSalvar) {
        try {
          await leiloeiroService.adicionarAoLeilao(organizadorId, id, ll)
        } catch (e) {
          if (e.response?.status === 409) {
            try {
              const lista = await leiloeiroService.listarDoLeilao(organizadorId, id)
              const vinculo = lista.find(l => l.leiloeiro_id === ll.leiloeiro_id)
              if (vinculo) await leiloeiroService.atualizarNoLeilao(organizadorId, id, vinculo.id, ll)
            } catch (e2) { console.error('Erro ao atualizar leiloeiro:', e2) }
          } else {
            console.error('Erro ao vincular leiloeiro:', e)
          }
        }
      }

      // Se tem novo banner, fazer upload
      if (formData.banner_file) {
        try {
          const uploadResult = await leiloesService.uploadBanner(organizadorId, id, formData.banner_file)
          console.log('Upload resultado:', uploadResult)
          success('Leilão e banner atualizados com sucesso!')
        } catch (bannerError) {
          console.error('Erro no upload do banner:', bannerError)
          error('Leilão atualizado, mas erro no upload do banner: ' + (bannerError.response?.data?.detail || bannerError.message))
        }
      } else {
        success('Leilão atualizado com sucesso!')
      }
      
      setTimeout(() => {
        window.location.href = `/leiloes/${id}`
      }, 1500)
      
    } catch (err) {
      console.error('Erro ao atualizar leilão:', err)
      const errorMessage = err.response?.data?.detail || 'Erro ao atualizar leilão'
      error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const getTipoLabel = (tipo) => {
    const tipos = {
      'gado_corte': 'Gado de Corte',
      'gado_leite': 'Gado de Leite',
      'gado_elite': 'Gado de Elite',
      'cavalo': 'Cavalo',
      'beneficente': 'Beneficente'
    }
    return tipos[tipo] || tipo
  }

  const isPremium = (tipo) => {
    return ['gado_leite', 'gado_elite'].includes(tipo)
  }

  const nextStep = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1)
  }

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1)
  }

  const showGtaNota = formData.fluxo_comissao === 'gta_nota'

  const handleSaveComissao = async (comissaoData) => {
    try {
      setLoadingComissao(true)
      const response = await comissoesService.criar(organizadorId, comissaoData)
      const novaComissao = response.data || response
      
      // Atualizar lista de comissões
      setOrganizadorConfig(prev => ({
        ...prev,
        comissoes: [...(prev?.comissoes || []), novaComissao]
      }))
      
      // Selecionar a nova comissão
      setFormData(prev => ({ ...prev, comissao_padrao_id: String(novaComissao.id) }))
      
      setShowNovaComissao(false)
      success('Comissão criada com sucesso!')
    } catch (err) {
      console.error('Erro ao criar comissão:', err)
      error('Erro ao criar comissão')
    } finally {
      setLoadingComissao(false)
    }
  }

  const handleSaveMeioPagamento = async (meioPagamentoData) => {
    const novoMeio = await meiosPagamentoService.criar(organizadorId, meioPagamentoData)
    const meio = novoMeio.data || novoMeio

    setMeiosPagamento(prev => [...prev, meio])
    setFormData(prev => ({ ...prev, meio_pagamento_padrao_id: meio.id }))
    setShowNovoMeioPagamento(false)
    success('Meio de pagamento criado com sucesso!')
  }

  const handleSaveLeiloeiro = async (leiloeiroData) => {
    try {
      setLoadingLeiloeiro(true)
      const novoLeiloeiro = await leiloeiroService.create(organizadorId, leiloeiroData)
      
      // Atualizar lista de leiloeiros
      setOrganizadorConfig(prev => ({
        ...prev,
        leiloeiros: [...(prev?.leiloeiros || []), novoLeiloeiro]
      }))
      
      // Selecionar o novo leiloeiro
      setFormData(prev => ({
        ...prev,
        leiloeiro_id: String(novoLeiloeiro.id),
        leiloeiro_perc_venda: novoLeiloeiro.perc_venda,
        leiloeiro_perc_defesa: novoLeiloeiro.perc_defesa
      }))
      
      setShowNovoLeiloeiro(false)
      success('Leiloeiro criado com sucesso!')
    } catch (err) {
      console.error('Erro ao criar leiloeiro:', err)
      error('Erro ao criar leiloeiro')
    } finally {
      setLoadingLeiloeiro(false)
    }
  }

  const handleEditComissao = async (comissaoData) => {
    try {
      setLoadingComissao(true)
      const response = await comissoesService.atualizar(organizadorId, formData.comissao_padrao_id, comissaoData)
      const comissaoAtualizada = response.data || response
      
      // Atualizar lista de comissões
      setOrganizadorConfig(prev => ({
        ...prev,
        comissoes: prev.comissoes.map(c => c.id === comissaoAtualizada.id ? comissaoAtualizada : c)
      }))
      
      setShowEditComissao(false)
      success('Comissão atualizada com sucesso!')
    } catch (err) {
      console.error('Erro ao atualizar comissão:', err)
      if (err.response?.status === 409) {
        error('Não é possível editar esta comissão pois ela já foi utilizada em lotes faturados. Alterar os valores afetaria o faturamento e as parcelas.')
      } else {
        error('Erro ao atualizar comissão')
      }
    } finally {
      setLoadingComissao(false)
    }
  }

  const handleEditLeiloeiro = async (leiloeiroData) => {
    try {
      setLoadingLeiloeiro(true)
      const leiloeiroAtualizado = await leiloeiroService.update(organizadorId, formData.leiloeiro_id, leiloeiroData)
      
      // Atualizar lista de leiloeiros
      setOrganizadorConfig(prev => ({
        ...prev,
        leiloeiros: prev.leiloeiros.map(l => l.id === leiloeiroAtualizado.id ? leiloeiroAtualizado : l)
      }))
      
      // Atualizar formData
      setFormData(prev => ({
        ...prev,
        leiloeiro_perc_venda: leiloeiroAtualizado.perc_venda,
        leiloeiro_perc_defesa: leiloeiroAtualizado.perc_defesa
      }))
      
      setShowEditLeiloeiro(false)
      success('Leiloeiro atualizado com sucesso!')
    } catch (err) {
      console.error('Erro ao atualizar leiloeiro:', err)
      error('Erro ao atualizar leiloeiro')
    } finally {
      setLoadingLeiloeiro(false)
    }
  }

  if (loadingData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rematei-blue"></div>
      </div>
    )
  }

  return (
    <>
      {/* Toasts */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => hideToast(toast.id)}
        />
      ))}

      <div className="page-content">
        {/* Header Padrão */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl px-6 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.location.href = `/leiloes/${id}`}
              className="p-2 rounded-xl text-white hover:bg-white/20 transition-all duration-200"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Editar Leilão</h1>
              <p className="text-blue-100 mt-1">
                Atualize as informações do leilão
              </p>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-4 py-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                const Icon = step.icon
                const isActive = currentStep === step.id
                const isCompleted = currentStep > step.id

                return (
                  <div key={step.id} className="flex items-center flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(step.id)}
                      className={`flex items-center space-x-1 sm:space-x-3 p-1 sm:p-2 rounded-lg transition-colors hover:bg-gray-50 ${
                        isActive ? 'text-rematei-blue' : isCompleted ? 'text-green-600' : 'text-gray-400'
                      }`}
                    >
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${
                        isActive ? 'border-rematei-blue bg-rematei-blue text-white' :
                        isCompleted ? 'border-green-600 bg-green-600 text-white' :
                        'border-gray-300 bg-white'
                      }`}>
                        {isCompleted ? <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" /> : <Icon className="w-4 h-4 sm:w-5 sm:h-5" />}
                      </div>
                      <div className="hidden md:block">
                        <p className="font-medium">{step.name}</p>
                        <p className="text-xs text-gray-500">{step.description}</p>
                      </div>
                    </button>
                    {index < steps.length - 1 && (
                      <div className={`w-3 sm:w-16 h-0.5 mx-1 sm:mx-4 flex-shrink-0 ${
                        isCompleted ? 'bg-green-600' : 'bg-gray-300'
                      }`} />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Input file fora do form para evitar submit */}
        <input
          type="file"
          accept="image/*"
          onChange={handleBannerUpload}
          className="hidden"
          id="banner-upload"
        />
        
        {/* Form Content */}
        <div>
            {/* Step 1: Básico */}
            {currentStep === 1 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome do Leilão *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      className="input-rematei text-lg"
                      placeholder={isOrgFluxoCavalo ? "Ex: Leilão de Cavalos - Haras Boa Vista" : "Ex: Leilão de Gado de Corte - Fazenda São João"}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de Leilão *
                      </label>
                      <select
                        required
                        value={formData.tipo_leilao}
                        onChange={(e) => setFormData({...formData, tipo_leilao: e.target.value})}
                        className="input-rematei"
                      >
                        {tiposParaExibir.map(tipo => (
                          <option key={tipo} value={tipo}>
                            {getTipoLabel(tipo)} {isPremium(tipo) ? '(Premium)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                        className="input-rematei"
                      >
                        <option value="on">Ativo</option>
                        <option value="off">Inativo</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Código do Evento
                    </label>
                    <input
                      type="text"
                      value={formData.codigo_evento}
                      onChange={(e) => setFormData({...formData, codigo_evento: e.target.value.toUpperCase()})}
                      className="input-rematei"
                      placeholder="Ex: LEI2024001 (opcional)"
                      maxLength="50"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Código único para identificar o evento (opcional)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Local & Data */}
            {currentStep === 2 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Data do Leilão *
                      </label>
                      <input
                        type="date"
                        required
                        value={formData.data_leilao}
                        onChange={(e) => setFormData({...formData, data_leilao: e.target.value})}
                        className="input-rematei"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hora de Início *
                      </label>
                      <input
                        type="time"
                        required
                        value={formData.hora_inicio}
                        onChange={(e) => setFormData({...formData, hora_inicio: e.target.value})}
                        className="input-rematei"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Horário Limite de Entrada de Animais
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Lotes cadastrados após este horário serão marcados como <strong>pós-horário</strong> e ficam ao fim da ordem de entrada.
                    </p>
                    <div className="flex items-center gap-3">
                      <input
                        type="time"
                        value={formData.horario_limite_entrada}
                        onChange={(e) => setFormData({...formData, horario_limite_entrada: e.target.value})}
                        className="input-rematei w-40"
                      />
                      {formData.horario_limite_entrada && (
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, horario_limite_entrada: ''})}
                          className="text-sm text-red-500 hover:underline"
                        >
                          Remover limite
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="showDataFim"
                      checked={showDataFim}
                      onChange={(e) => setShowDataFim(e.target.checked)}
                      className="h-4 w-4 text-rematei-blue focus:ring-rematei-blue border-gray-300 rounded"
                    />
                    <label htmlFor="showDataFim" className="text-sm font-medium text-gray-700">
                      Definir data e hora de fim
                    </label>
                  </div>

                  {showDataFim && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-blue-50 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Data de Fim
                        </label>
                        <input
                          type="date"
                          value={formData.data_fim}
                          onChange={(e) => setFormData({...formData, data_fim: e.target.value})}
                          className="input-rematei"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Hora de Fim
                        </label>
                        <input
                          type="time"
                          value={formData.hora_fim}
                          onChange={(e) => setFormData({...formData, hora_fim: e.target.value})}
                          className="input-rematei"
                        />
                      </div>
                    </div>
                  )}

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cidade *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        required
                        value={searchCidade}
                        onChange={(e) => setSearchCidade(e.target.value)}
                        className="input-rematei pr-10"
                        placeholder="Digite para buscar a cidade..."
                      />
                      <Search className="absolute right-3 top-3 w-4 h-4 text-gray-400" />
                    </div>
                    
                    {cidades.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {cidades.map((cidade, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => handleCidadeSelect(cidade)}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex justify-between items-center"
                          >
                            <span>{cidade.nome}</span>
                            <span className="text-sm text-gray-500">{cidade.uf}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        UF *
                      </label>
                      <input
                        type="text"
                        required
                        maxLength="2"
                        value={formData.uf}
                        onChange={(e) => setFormData({...formData, uf: e.target.value.toUpperCase()})}
                        className="input-rematei"
                        placeholder="Ex: MG"
                        readOnly={formData.cidade}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Local
                      </label>
                      <input
                        type="text"
                        value={formData.local}
                        onChange={(e) => setFormData({...formData, local: e.target.value})}
                        className="input-rematei"
                        placeholder="Ex: Centro de Eventos"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Mídia */}
            {currentStep === 3 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Link da Transmissão (YouTube)
                    </label>
                    <input
                      type="url"
                      value={formData.link_transmissao}
                      onChange={(e) => setFormData({...formData, link_transmissao: e.target.value})}
                      className="input-rematei"
                      placeholder="https://youtube.com/watch?v=..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-4">
                      Banner do Leilão
                    </label>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Upload Area */}
                      <div>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                          <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600 mb-2">
                            {formData.banner_url ? 'Alterar banner' : 'Clique para fazer upload'}
                          </p>
                          <p className="text-xs text-gray-500 mb-4">
                            PNG, JPG até 5MB
                          </p>

                          <label
                            htmlFor="banner-upload"
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                          >
                            {formData.banner_url ? 'Alterar Banner' : 'Selecionar Arquivo'}
                          </label>
                          {formData.banner_file && (
                            <div className="mt-3 flex items-center justify-center space-x-2">
                              <p className="text-sm text-green-600">
                                ✓ {formData.banner_file.name}
                              </p>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  setFormData({ ...formData, banner_file: null })
                                }}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Preview Area */}
                      <div>
                        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 min-h-[200px] flex items-center justify-center">
                          {formData.banner_file ? (
                            <div className="text-center">
                              <p className="text-sm text-gray-600 mb-3">Novo banner:</p>
                              <img
                                src={URL.createObjectURL(formData.banner_file)}
                                alt="Preview do novo banner"
                                className="max-w-full max-h-48 rounded shadow-sm mx-auto"
                              />
                            </div>
                          ) : formData.banner_url ? (
                            <div className="text-center">
                              <p className="text-sm text-gray-600 mb-3">Banner atual:</p>
                              <div className="relative">
                                <img
                                  src={formData.banner_url}
                                  alt="Banner atual"
                                  className="max-w-full max-h-48 rounded shadow-sm mx-auto"
                                  onError={(e) => {
                                    console.error('Erro ao carregar banner:', formData.banner_url)
                                    e.target.parentElement.innerHTML = '<p class="text-red-500 text-sm">Erro ao carregar banner</p>'
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => setFormData({...formData, banner_url: ''})}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center text-gray-400">
                              <div className="w-16 h-16 mx-auto mb-2 bg-gray-200 rounded flex items-center justify-center">
                                <Upload className="w-8 h-8" />
                              </div>
                              <p className="text-sm">Preview aparecerá aqui</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            )}

            {/* Step 4: Financeiro */}
            {currentStep === 4 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fluxo de Comissão
                    </label>
                    <div className="input-rematei bg-gray-50 text-gray-700">
                      { { 'primeira_parcela': 'Comissão na Primeira Parcela', 'gta_nota': 'GTA + Nota', 'separada': 'Comissão Separada', 'parcelada': 'Comissão Parcelada', 'cavalo': '🐴 Cavalo' }[formData.fluxo_comissao] || 'Comissão na Primeira Parcela' }
                    </div>
                    <input type="hidden" name="fluxo_comissao" value={formData.fluxo_comissao || 'primeira_parcela'} />
                  </div>

                  {formData.tipo_leilao === 'beneficente' && (
                    <ComissaoBeneficenteOpcao
                      respeitarIndividual={formData.comissao_respeitar_individual}
                      onChange={(v) => setFormData(prev => ({ ...prev, comissao_respeitar_individual: v }))}
                    />
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Comissão Padrão {formData.tipo_leilao !== 'beneficente' && '*'}
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowNovaComissao(true)}
                          className="text-xs text-rematei-blue hover:text-rematei-blue-dark"
                        >
                          + Nova Comissão
                        </button>
                      </div>
                      <select
                        key={`comissao-sel-${organizadorConfig?.comissoes?.length || 0}`}
                        required={formData.tipo_leilao !== 'beneficente'}
                        value={formData.comissao_padrao_id}
                        onChange={(e) => {
                          const comissao = organizadorConfig?.comissoes?.find(c => String(c.id) === e.target.value)
                          setFormData({
                            ...formData,
                            comissao_padrao_id: e.target.value,
                            comissao_venda: comissao?.comissao_venda || formData.comissao_venda,
                            comissao_compra: comissao?.comissao_compra || formData.comissao_compra,
                            comissao_defesa: comissao?.comissao_defesa || formData.comissao_defesa
                          })
                        }}
                        className="input-rematei"
                      >
                        <option value="">Selecione uma comissão</option>
                        {organizadorConfig?.comissoes?.map(comissao => (
                          <option key={comissao.id} value={String(comissao.id)}>
                            {comissao.nome} {comissao.tipo_comissao === 'socios' ? '(Sócios)' : `(V:${comissao.comissao_venda}% C:${comissao.comissao_compra}% D:${comissao.comissao_defesa}%)`}
                          </option>
                        ))}
                      </select>

                      {formData.comissao_padrao_id && (
                        <div className="mt-3 p-4 bg-blue-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h4 className="font-medium text-gray-700">Comissão Selecionada</h4>
                              {(() => {
                                const comissao = organizadorConfig?.comissoes?.find(c => String(c.id) === String(formData.comissao_padrao_id))
                                if (!comissao) return null

                                if (comissao.tipo_comissao === 'socios') {
                                  return (
                                    <div className="text-sm text-gray-600 space-y-1">
                                      <div className="font-medium text-purple-700">Comissão por Sócios:</div>
                                      <div>👥 Ambos: C:{comissao.ambos_socios_comprador || 0}% V:{comissao.ambos_socios_vendedor || 0}% D:{comissao.ambos_socios_defesa || 0}%</div>
                                      <div>👤 Só Comp: C:{comissao.comprador_socio_comprador || 0}% V:{comissao.comprador_socio_vendedor || 0}% D:{comissao.comprador_socio_defesa || 0}%</div>
                                      <div>👤 Só Vend: C:{comissao.vendedor_socio_comprador || 0}% V:{comissao.vendedor_socio_vendedor || 0}% D:{comissao.vendedor_socio_defesa || 0}%</div>
                                      <div>❌ Nenhum: C:{comissao.comissao_compra || 0}% V:{comissao.comissao_venda || 0}% D:{comissao.comissao_defesa || 0}%</div>
                                    </div>
                                  )
                                } else {
                                  return (
                                    <p className="text-sm text-gray-600">
                                      V:{comissao.comissao_venda || '0'}% | C:{comissao.comissao_compra || '0'}% | D:{comissao.comissao_defesa || '0'}%
                                    </p>
                                  )
                                }
                              })()}
                            </div>
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const response = await comissoesService.verificarEmUso(organizadorId, formData.comissao_padrao_id)
                                  const { em_uso, qtd_lotes } = response.data || response
                                  if (em_uso) {
                                    error(`Não é possível editar esta comissão pois ela já foi utilizada em ${qtd_lotes} lote(s) faturado(s). Alterar os valores afetaria o faturamento e as parcelas.`)
                                    return
                                  }
                                } catch (err) {
                                  console.error('Erro ao verificar uso da comissão:', err)
                                }
                                setShowEditComissao(true)
                              }}
                              className="text-xs text-rematei-blue hover:text-rematei-blue-dark"
                            >
                              Editar
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      {/* Cabeçalho com toggle de 2 leiloeiros */}
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Leiloeiro{formData.tipo_leilao !== 'beneficente' && ' *'}
                        </label>
                        <div className="flex items-center gap-3">
                          {(organizadorConfig?.leiloeiros || []).length > 1 && formData.leiloeiro_id && (
                            <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={doisLeiloeiros}
                                onChange={e => {
                                  setDoisLeiloeiros(e.target.checked)
                                  if (!e.target.checked) setFormData(prev => ({ ...prev, leiloeiro2_id: '', leiloeiro2_perc_venda: '', leiloeiro2_perc_defesa: '' }))
                                }}
                                className="rounded"
                              />
                              2 leiloeiros
                            </label>
                          )}
                          <button type="button" onClick={() => setShowNovoLeiloeiro(true)} className="text-xs text-rematei-blue hover:text-rematei-blue-dark">
                            + Novo Leiloeiro
                          </button>
                        </div>
                      </div>

                      {/* Dropdowns lado a lado */}
                      <div className={doisLeiloeiros ? 'grid grid-cols-2 gap-2' : ''}>
                        <select
                          key={`leiloeiro-sel-${organizadorConfig?.leiloeiros?.length || 0}`}
                          required={formData.tipo_leilao !== 'beneficente'}
                          value={formData.leiloeiro_id}
                          onChange={(e) => {
                            const leiloeiro = organizadorConfig?.leiloeiros?.find(l => String(l.id) === e.target.value)
                            setFormData(prev => ({
                              ...prev,
                              leiloeiro_id: e.target.value,
                              leiloeiro_perc_venda:  leiloeiro?.perc_venda  || prev.leiloeiro_perc_venda,
                              leiloeiro_perc_defesa: leiloeiro?.perc_defesa || prev.leiloeiro_perc_defesa,
                              leiloeiro2_id: '', leiloeiro2_perc_venda: '', leiloeiro2_perc_defesa: '',
                            }))
                            setDoisLeiloeiros(false)
                          }}
                          className="input-rematei"
                        >
                          <option value="">Selecione um leiloeiro</option>
                          {organizadorConfig?.leiloeiros?.map(l => (
                            <option key={l.id} value={String(l.id)}>{l.nome}</option>
                          ))}
                        </select>

                        {doisLeiloeiros && (
                          <select
                            key={`leiloeiro2-sel-${organizadorConfig?.leiloeiros?.length || 0}`}
                            value={formData.leiloeiro2_id}
                            onChange={(e) => {
                              const l2 = organizadorConfig?.leiloeiros?.find(l => String(l.id) === e.target.value)
                              setFormData(prev => ({
                                ...prev,
                                leiloeiro2_id:          e.target.value,
                                leiloeiro2_perc_venda:  l2?.perc_venda  ?? '',
                                leiloeiro2_perc_defesa: l2?.perc_defesa ?? '',
                              }))
                            }}
                            className="input-rematei"
                          >
                            <option value="">2º leiloeiro</option>
                            {(organizadorConfig?.leiloeiros || [])
                              .filter(l => String(l.id) !== String(formData.leiloeiro_id))
                              .map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)
                            }
                          </select>
                        )}
                      </div>

                      {formData.leiloeiro_id && (
                        <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-gray-700">Leiloeiro Padrão</h4>
                              <p className="text-sm text-gray-600">
                                Venda:{formData.leiloeiro_perc_venda || '0'}% | Defesa:{formData.leiloeiro_perc_defesa || '0'}%
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowEditLeiloeiro(true)}
                              className="text-xs text-rematei-blue hover:text-rematei-blue-dark"
                            >
                              Editar
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Promotores do Leilão — vira "Assessoria" no fluxo cavalo */}
                  <div className="mt-2">
                    <PromotoresLeilao
                      organizadorId={organizadorId}
                      leilaoId={id}
                      promotoresHabilitado={organizadorAtivo?.promotores_habilitado}
                      titulo={isOrgFluxoCavalo ? 'Promotores (Leilão)' : 'Promotores do Leilão'}
                      labelItem="Promotor"
                    />
                  </div>

                  {/* Regra de Faturamento */}
                  {formData.tipo_leilao !== 'beneficente' && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-medium text-gray-800 mb-3">Regra de Faturamento</h4>
                      <div className="flex space-x-3 mb-4">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, regra_faturamento: 'por_cabeca', meio_pagamento_padrao_id: '' }))}
                          className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium border-2 transition-colors ${
                            formData.regra_faturamento === 'por_cabeca'
                              ? 'border-green-600 bg-green-600 text-white'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-green-300'
                          }`}
                        >
                          Faturamento por Cabeça
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, regra_faturamento: 'por_parcela' }))}
                          className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium border-2 transition-colors ${
                            formData.regra_faturamento === 'por_parcela'
                              ? 'border-green-600 bg-green-600 text-white'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-green-300'
                          }`}
                        >
                          Faturamento por Parcela
                        </button>
                      </div>

                      {formData.regra_faturamento === 'por_cabeca' && (
                        <p className="text-sm text-gray-600">
                          O valor de venda será calculado por cabeça × quantidade de animais (padrão).
                        </p>
                      )}

                      {formData.regra_faturamento === 'por_parcela' && (
                        <div className="space-y-4">
                          <p className="text-sm text-gray-600">
                            O valor de venda será um valor fixo por parcela. É obrigatório selecionar um meio de pagamento.
                          </p>

                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-sm font-medium text-gray-700">
                                Meio de Pagamento *
                              </label>
                              <button
                                type="button"
                                onClick={() => setShowNovoMeioPagamento(true)}
                                className="text-xs text-rematei-blue hover:text-rematei-blue-dark"
                              >
                                + Novo Meio de Pagamento
                              </button>
                            </div>
                            <select
                              value={formData.meio_pagamento_padrao_id}
                              onChange={(e) => setFormData(prev => ({ ...prev, meio_pagamento_padrao_id: e.target.value }))}
                              className="input-rematei"
                              required
                            >
                              <option value="">Selecione um meio de pagamento</option>
                              {meiosPagamento.map(meio => (
                                <option key={meio.id} value={meio.id}>
                                  {meio.nome} ({meio.tipo === 'A_VISTA' ? 'À Vista' : meio.tipo === 'PRAZO' ? 'A Prazo' : 'Parcelado'})
                                </option>
                              ))}
                            </select>
                            {meiosPagamento.length === 0 && (
                              <p className="text-xs text-amber-600 mt-1">
                                Nenhum meio de pagamento cadastrado.{' '}
                                <button
                                  type="button"
                                  onClick={() => setShowNovoMeioPagamento(true)}
                                  className="underline font-medium"
                                >
                                  Cadastrar agora
                                </button>
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Dado Bancário do Organizador para este Leilão */}
                  {formData.tipo_leilao !== 'beneficente' && dadosBancarios.length > 0 && (
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                        <DollarSign size={16} className="text-purple-600" />
                        Dado Bancário para Comissão
                      </h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Conta bancária que aparecerá nos relatórios deste leilão para recebimento de comissões.
                      </p>
                      <select
                        value={formData.dado_bancario_organizador_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, dado_bancario_organizador_id: e.target.value }))}
                        className="input-rematei"
                      >
                        <option value="">Selecione uma conta bancária</option>
                        {dadosBancarios.map(db => (
                          <option key={db.id} value={db.id}>
                            {db.banco} - AG {db.agencia} - CC {db.conta}
                            {db.pix ? ` (PIX: ${db.pix})` : ''}
                            {db.is_principal ? ' ⭐ Principal' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {showGtaNota && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4 bg-amber-50 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor por Animal (R$)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.valor_por_animal}
                          disabled
                          className="input-rematei bg-gray-100 cursor-not-allowed"
                          placeholder="0.00"
                        />
                        <p className="text-xs text-gray-500 mt-1">Configurado em Cadastros &gt; GTA e Nota</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor por GTA/Nota (R$)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.valor_nota}
                          disabled
                          className="input-rematei bg-gray-100 cursor-not-allowed"
                          placeholder="0.00"
                        />
                        <p className="text-xs text-gray-500 mt-1">Configurado em Cadastros &gt; GTA e Nota</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Modo de Cobrança
                        </label>
                        <select
                          value={formData.gta_modo_cobranca || ''}
                          disabled
                          className="input-rematei bg-gray-100 cursor-not-allowed"
                        >
                          <option value="">Usar padrão do organizador</option>
                          <option value="por_vendedor">Por Vendedor</option>
                          <option value="por_lote">Por Lote</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Configurado em Cadastros &gt; GTA e Nota</p>
                      </div>
                    </div>
                  )}

                  {formData.tipo_leilao === 'cavalo' && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <h4 className="font-medium text-gray-800 mb-2">🐴 Configuração Defesa e Taxa de Inscrição</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Modo de Defesa
                          </label>
                          <div className="flex flex-col sm:flex-row gap-1.5 sm:gap-3">
                            <label className="flex items-center text-sm whitespace-nowrap">
                              <input
                                type="radio"
                                value="tabela"
                                checked={formData.defesa_modo === 'tabela'}
                                onChange={(e) => setFormData({...formData, defesa_modo: e.target.value})}
                                className="mr-1.5"
                              />
                              Tabela por Faixa
                              <span
                                className="ml-1 text-gray-400 cursor-help"
                                title="A defesa vira um valor fixo em R$, definido conforme em qual faixa de valor de venda o lote se encaixa (configurado logo abaixo, em 'Defesa por Faixa de Valor'). Não usa percentual."
                              >
                                ⓘ
                              </span>
                            </label>
                            <label className="flex items-center text-sm whitespace-nowrap">
                              <input
                                type="radio"
                                value="percentual"
                                checked={formData.defesa_modo === 'percentual'}
                                onChange={(e) => setFormData({...formData, defesa_modo: e.target.value})}
                                className="mr-1.5"
                              />
                              Percentual
                            </label>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Taxa de Inscrição (R$)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.taxa_inscricao_valor}
                            onChange={(e) => setFormData({...formData, taxa_inscricao_valor: e.target.value})}
                            className="input-rematei"
                            placeholder="Opcional"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Cobrada do vendedor por lote. Deixe em branco para não cobrar.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.defesa_modo !== 'percentual' && (
                    <FaixasDefesaLeilao
                      organizadorId={organizadorId}
                      leilaoId={id}
                      tipoLeilao={formData.tipo_leilao}
                    />
                  )}
                </div>
              </div>
            )}

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`px-6 py-2 rounded-lg font-medium ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Anterior
            </button>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => window.location.href = `/leiloes/${id}`}
                className="btn-secondary"
              >
                Cancelar
              </button>

              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="btn-primary"
                >
                  Próximo
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    handleSubmit(e)
                  }}
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Salvar Alterações
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <NovaComissaoModal
        isOpen={showNovaComissao}
        onClose={() => setShowNovaComissao(false)}
        onSave={handleSaveComissao}
        loading={loadingComissao}
      />

      <NovoLeiloeiroModal
        isOpen={showNovoLeiloeiro}
        onClose={() => setShowNovoLeiloeiro(false)}
        onSave={handleSaveLeiloeiro}
        loading={loadingLeiloeiro}
      />

      <EditarComissaoModal
        isOpen={showEditComissao}
        onClose={() => setShowEditComissao(false)}
        onSave={handleEditComissao}
        comissao={organizadorConfig?.comissoes?.find(c => c.id === parseInt(formData.comissao_padrao_id))}
        loading={loadingComissao}
      />

      <EditarLeiloeiroModal
        isOpen={showEditLeiloeiro}
        onClose={() => setShowEditLeiloeiro(false)}
        onSave={handleEditLeiloeiro}
        leiloeiro={organizadorConfig?.leiloeiros?.find(l => l.id === parseInt(formData.leiloeiro_id))}
        loading={loadingLeiloeiro}
      />

      <NovoMeioPagamentoModal
        isOpen={showNovoMeioPagamento}
        onClose={() => setShowNovoMeioPagamento(false)}
        onSave={handleSaveMeioPagamento}
      />
    </>
  )
}